import { Component, EventEmitter, HostBinding, Input, Output } from '@angular/core';
import { PanelStateLayout } from '../../models/panel-state-layout.model';
import { PanelStateMode } from '../../models/panel-state-mode.model';

const ERROR_ICON = 'assets/icons/triangle-with-exclamation-mark.svg';

/**
 * The state a DEO panel (chats, projects, artifacts, and the sidebar chat lists) shows instead of, or
 * above, its rows.
 *
 * In 'error' mode it shows the backend's message — with the support reference code already stripped by
 * getApiErrorMessage / getResponseErrorMessage — plus a Try again button that re-runs only that panel's
 * load. The failure stays inside the panel so the rest of the assistant (an open conversation above all)
 * keeps working, which a navigation to /error would destroy.
 *
 * 'banner' layout exists for the case where the panel still holds rows: the failure is reported above them
 * instead of replacing them.
 */
@Component({
  standalone: false,
  selector: 'deo-panel-state',
  templateUrl: './deo-panel-state.component.html',
  styleUrls: ['./deo-panel-state.component.scss']
})
export class DeoPanelStateComponent {
  @Input() mode: PanelStateMode = 'empty';
  /** Icon for the empty state; the error state always uses the warning icon. */
  @Input() icon = 'assets/icons/chat.svg';
  @Input() title = '';
  /** Backend failure message, shown in 'error' mode only. */
  @Input() message = '';
  /** Tightens the vertical padding for panels that sit in a shorter container. */
  @Input() compact = false;
  /** 'block' fills and centres; 'banner' is a slim row above rows that already exist. */
  @Input() layout: PanelStateLayout = 'block';

  @Output() retry$ = new EventEmitter<void>();

  /**
   * Only the block layout may grow. A banner that kept `flex: 1` would eat the panel's column and push
   * the list it sits above out of view — the same trap the empty flex list containers caused.
   */
  @HostBinding('class.deo-panel-state--banner')
  protected get isBanner(): boolean {
    return this.layout === 'banner';
  }

  protected get isError(): boolean {
    return this.mode === 'error';
  }

  protected get resolvedIcon(): string {
    return this.isError ? ERROR_ICON : this.icon;
  }
}
